import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  WompiWebhookRequest,
  WompiWebhookRequestDetail,
  WompiWebhookRequestFilter,
  WompiWebhookStats,
  WompiRequestStatus,
  WompiPagedResult
} from '../interfaces/wompi-webhook.interfaces';

@Injectable({
  providedIn: 'root'
})
export class WompiWebhookService {
  private apiUrl = `${environment.apiUrl}/wompi-requests`;

  constructor(private http: HttpClient) { }

  /**
   * Obtener todas las solicitudes con filtros opcionales
   */
  getRequests(filter?: WompiWebhookRequestFilter): Observable<WompiWebhookRequest[]> {
    let params = new HttpParams();

    if (filter) {
      if (filter.status !== undefined && filter.status !== null) {
        params = params.set('status', filter.status.toString());
      }
      if (filter.transactionId) {
        params = params.set('transactionId', filter.transactionId);
      }
      if (filter.reference) {
        params = params.set('reference', filter.reference);
      }
      if (filter.customerEmail) {
        params = params.set('customerEmail', filter.customerEmail);
      }
      if (filter.transactionStatus) {
        params = params.set('transactionStatus', filter.transactionStatus);
      }
      if (filter.paymentMethodType) {
        params = params.set('paymentMethodType', filter.paymentMethodType);
      }
      if (filter.fromDate) {
        params = params.set('fromDate', new Date(filter.fromDate).toISOString());
      }
      if (filter.toDate) {
        params = params.set('toDate', new Date(filter.toDate).toISOString());
      }
      if (filter.failureStep) {
        params = params.set('failureStep', filter.failureStep);
      }
      if (filter.environment) {
        params = params.set('environment', filter.environment);
      }
      if (filter.paymentLinkId) {
        params = params.set('paymentLinkId', filter.paymentLinkId);
      }
      if (filter.paymentLinkIdInternal) {
        params = params.set('paymentLinkIdInternal', filter.paymentLinkIdInternal.toString());
      }
    }

    return this.http.get<WompiWebhookRequest[]>(this.apiUrl, { params });
  }

  /**
   * Obtener solicitudes paginadas con filtros server-side
   */
  getRequestsPaged(filter: WompiWebhookRequestFilter): Observable<WompiPagedResult> {
    let params = new HttpParams();

    // Parámetros de paginación
    if (filter.pageNumber) {
      params = params.set('pageNumber', filter.pageNumber.toString());
    }
    if (filter.pageSize) {
      params = params.set('pageSize', filter.pageSize.toString());
    }
    if (filter.searchTerm) {
      params = params.set('searchTerm', filter.searchTerm);
    }
    if (filter.sortBy) {
      params = params.set('sortBy', filter.sortBy);
    }
    if (filter.sortDirection) {
      params = params.set('sortDirection', filter.sortDirection);
    }

    // Filtros específicos
    if (filter.status !== undefined && filter.status !== null) {
      params = params.set('status', filter.status.toString());
    }
    if (filter.transactionStatus) {
      params = params.set('transactionStatus', filter.transactionStatus);
    }
    if (filter.paymentMethodType) {
      params = params.set('paymentMethodType', filter.paymentMethodType);
    }
    if (filter.fromDate) {
      params = params.set('fromDate', new Date(filter.fromDate).toISOString());
    }
    if (filter.toDate) {
      params = params.set('toDate', new Date(filter.toDate).toISOString());
    }
    if (filter.failureStep) {
      params = params.set('failureStep', filter.failureStep);
    }
    if (filter.environment) {
      params = params.set('environment', filter.environment);
    }

    return this.http.get<WompiPagedResult>(`${this.apiUrl}/paged`, { params });
  }

  /**
   * Obtener detalle completo de una solicitud
   */
  getRequestById(id: number): Observable<WompiWebhookRequestDetail> {
    return this.http.get<WompiWebhookRequestDetail>(`${this.apiUrl}/${id}`);
  }

  /**
   * Reprocesar una solicitud fallida
   */
  retryProcessing(id: number): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(`${this.apiUrl}/${id}/retry`, {});
  }

  /**
   * Obtener estadísticas de las solicitudes
   */
  getStats(): Observable<WompiWebhookStats> {
    return this.http.get<WompiWebhookStats>(`${this.apiUrl}/stats`);
  }

  /**
   * Obtener el nombre del estado en español
   */
  getStatusName(status: WompiRequestStatus): string {
    switch (status) {
      case WompiRequestStatus.Received: return 'Recibido';
      case WompiRequestStatus.Processing: return 'Procesando';
      case WompiRequestStatus.Success: return 'Exitoso';
      case WompiRequestStatus.Failed: return 'Fallido';
      default: return 'Desconocido';
    }
  }

  /**
   * Obtener la clase de badge según el estado
   */
  getStatusBadgeClass(status: WompiRequestStatus): string {
    switch (status) {
      case WompiRequestStatus.Received: return 'badge bg-info text-white';
      case WompiRequestStatus.Processing: return 'badge bg-warning text-dark';
      case WompiRequestStatus.Success: return 'badge bg-success';
      case WompiRequestStatus.Failed: return 'badge bg-danger';
      default: return 'badge bg-secondary';
    }
  }

  /**
   * Obtener el icono según el estado
   */
  getStatusIcon(status: WompiRequestStatus): string {
    switch (status) {
      case WompiRequestStatus.Received: return 'bi-inbox';
      case WompiRequestStatus.Processing: return 'bi-arrow-repeat';
      case WompiRequestStatus.Success: return 'bi-check-circle-fill';
      case WompiRequestStatus.Failed: return 'bi-x-circle-fill';
      default: return 'bi-question-circle';
    }
  }

  /**
   * Formatear monto en centavos a pesos colombianos
   */
  formatAmount(amountInCents: number, currency: string = 'COP'): string {
    const amount = amountInCents / 100;
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(amount);
  }

  /**
   * Obtener color para el estado de transacción
   */
  getTransactionStatusColor(status: string): string {
    switch (status?.toUpperCase()) {
      case 'APPROVED': return 'text-green-600 dark:text-green-400';
      case 'DECLINED': return 'text-red-600 dark:text-red-400';
      case 'PENDING': return 'text-yellow-600 dark:text-yellow-400';
      case 'VOIDED': return 'text-gray-600 dark:text-gray-400';
      default: return 'text-gray-600 dark:text-gray-400';
    }
  }

  /**
   * Obtener icono para el método de pago
   */
  getPaymentMethodIcon(paymentMethodType: string): string {
    switch (paymentMethodType?.toUpperCase()) {
      case 'CARD': return 'bi-credit-card';
      case 'NEQUI': return 'bi-phone';
      case 'PSE': return 'bi-bank';
      case 'BANCOLOMBIA_TRANSFER': return 'bi-bank2';
      default: return 'bi-wallet2';
    }
  }
}
